import apiService from "../api-service";

/**
 * Download CSV report for a completed task
 * @param taskId - Task ID for which to generate the report
 * @param accessToken - Optional access token for authentication
 * @returns Promise that resolves when download is complete
 */
export async function downloadCSVReport(
  taskId: string,
  accessToken?: string,
): Promise<void> {
  if (!taskId || !taskId.trim()) {
    throw new Error("Task ID is required");
  }

  try {
    const response: any = await apiService.get({
      url: `reports/${taskId}/csv`,
      baseUrlKey: "apiUrl",
      accessToken,
      axiosConfig: {
        responseType: "blob",
        headers: {
          Accept: "text/csv",
        },
      },
    });

    if (response?.data) {
      // Create blob from response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "text/csv",
      });

      // Create download link and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qa-report-${taskId}.csv`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`CSV report downloaded successfully for task: ${taskId}`);
    } else {
      throw new Error("Failed to download CSV report");
    }
  } catch (error: any) {
    console.error(`Failed to download CSV report for task ${taskId}:`, error);
    throw new Error(
      error?.message || "Failed to download CSV report. Please try again.",
    );
  }
}

/**
 * Download PDF report for a completed task
 * @param taskId - Task ID for which to generate the report
 * @param accessToken - Optional access token for authentication
 * @returns Promise that resolves when download is complete
 */
export async function downloadPDFReport(
  taskId: string,
  accessToken?: string,
): Promise<void> {
  if (!taskId || !taskId.trim()) {
    throw new Error("Task ID is required");
  }

  try {
    const response: any = await apiService.get({
      url: `reports/${taskId}/pdf`,
      baseUrlKey: "apiUrl",
      accessToken,
      axiosConfig: {
        responseType: "blob",
        headers: {
          Accept: "application/pdf",
        },
      },
    });

    if (response?.data) {
      // Create blob from response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });

      // Create download link and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qa-report-${taskId}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`PDF report downloaded successfully for task: ${taskId}`);
    } else {
      throw new Error("Failed to download PDF report");
    }
  } catch (error: any) {
    console.error(`Failed to download PDF report for task ${taskId}:`, error);
    throw new Error(
      error?.message || "Failed to download PDF report. Please try again.",
    );
  }
}
